import { z } from 'zod';
import { sessionUserSchema } from './session.js';

export const USERNAME_PATTERN = /^[A-Za-z0-9_]{3,20}$/;
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 128;

export const usernameSchema = z
  .string()
  .regex(USERNAME_PATTERN, 'Use 3 to 20 letters, numbers or underscores.');

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, `Use at least ${PASSWORD_MIN} characters.`)
  .max(PASSWORD_MAX, `Use at most ${PASSWORD_MAX} characters.`);

/** Blank means "no email". Email is optional and only reserved for a future password reset. */
export const emailSchema = z
  .string()
  .trim()
  .max(254, 'That email is too long.')
  .pipe(z.union([z.literal(''), z.email('Enter a valid email address.')]))
  .transform((v) => (v === '' ? null : v))
  .nullish()
  .transform((v) => v ?? null);

export const registerRequestSchema = z
  .object({ username: usernameSchema, password: passwordSchema, email: emailSchema })
  .refine((v) => v.password.toLowerCase() !== v.username.toLowerCase(), {
    path: ['password'],
    message: 'The password must not be your username.',
  });
export type RegisterRequest = z.input<typeof registerRequestSchema>;

/** Login checks only that fields are present: the rules for new passwords must not lock out old ones. */
export const loginRequestSchema = z.object({
  username: z.string().min(1, 'Enter your username.').max(64),
  password: z.string().min(1, 'Enter your password.').max(PASSWORD_MAX),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const deleteAccountRequestSchema = z.object({
  password: z.string().min(1, 'Enter your password.').max(PASSWORD_MAX),
});
export type DeleteAccountRequest = z.infer<typeof deleteAccountRequestSchema>;

export const authResponseSchema = z.object({ user: sessionUserSchema });
export type AuthResponse = z.infer<typeof authResponseSchema>;

export const okResponseSchema = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof okResponseSchema>;
